import type { VehicleDock, VehicleLock } from "@/domain/vehicle";
import { generateCustomEndpoint } from "@/utils/utils";
import type { PaginatedResponse } from "@/v2/queries/types";
import { apiClient, apiClientJSON } from "../apiClient";

export interface Vehicle {
  id: number;
  code: string;
  name: string;
  status: string;
  lockStatus: string;
  battery: number;
  latitude: number;
  longitude: number;
  city?: { id: number; name?: string } | null;
  station?: { id: number; name?: string; code?: string } | null;
  linkedStation?: { id: number } | null;
  type: string;
  brand: string;
  lock?: VehicleLock | null;
  dock?: VehicleDock | null;
  partnerId?: number | null;
  lastSeen: string;
  createdAt: string;
  updatedAt: string;
}

export interface VehicleListParams {
  page?: number;
  search?: string;
  cityId?: number | null;
  sort?: string;
  filter?: string;
  size?: number;
  // Typed filters that flow directly to the Java VehicleFilter on the
  // bikesharing-site backend. Use these instead of the legacy comma-`:`
  // `filter` string when calling from new code.
  status?: string;
  vehicleType?: string;
  stationName?: string;
  partnerId?: number | null;
  // Mandatory for super-admin callers — the Java resolver only auto-fills
  // whiteLabelId from the SecurityPrincipal for WL-admin sessions, so a
  // super-admin omitting this leaks every fleet's vehicles back.
  whiteLabelId?: number | null;
}

export interface VehicleUpdate {
  status?: string;
  stationId?: number | null;
  name?: string;
  [key: string]: unknown;
}

export const getVehicles = (
  params: VehicleListParams = {}
): Promise<PaginatedResponse<Vehicle>> => {
  const {
    page = 0,
    search = "",
    cityId = null,
    sort = "",
    filter = "",
    size = 10,
    status,
    vehicleType,
    stationName,
    partnerId,
    whiteLabelId,
  } = params;
  let endpoint = generateCustomEndpoint(
    "/station-service/dashboard/vehicles?",
    page,
    search,
    cityId ? String(cityId) : "",
    sort,
    filter,
    String(size)
  );
  // Spring binds these as VehicleFilter fields. Encoded individually so
  // values with spaces (e.g. "Oslo Sentrum Demo Station") work correctly.
  if (status) endpoint += `&statuses=${encodeURIComponent(status)}`;
  if (vehicleType)
    endpoint += `&vehicleType=${encodeURIComponent(vehicleType)}`;
  if (stationName)
    endpoint += `&stationName=${encodeURIComponent(stationName)}`;
  if (partnerId != null) endpoint += `&partnerId=${partnerId}`;
  if (whiteLabelId != null) endpoint += `&whiteLabelId=${whiteLabelId}`;
  return apiClientJSON.get(endpoint).then((r) => r.data);
};

export const getVehicleById = (id: number): Promise<Vehicle> => {
  return apiClientJSON
    .get(`/station-service/dashboard/vehicles/${id}`)
    .then((r) => r.data);
};

export const getVehicleByCode = (code: string): Promise<Vehicle> => {
  return apiClientJSON
    .get(`/station-service/dashboard/vehicles/one?code=${code}`)
    .then((r) => r.data);
};

export const updateVehicle = (
  id: number,
  dto: VehicleUpdate
): Promise<Vehicle> => {
  // The backend's update endpoint is tri-state on partnerId:
  //   partnerId: 5            → assign partner 5
  //   partnerId: null + ?clearPartner=true → clear partner
  //   partnerId: null (alone) → no-op (leave existing partner unchanged)
  // The FE always intends the body to be the source of truth, so when we send
  // partnerId: null we have to opt in to the clearPartner query param.
  const params = dto.partnerId === null ? "?clearPartner=true" : "";
  return apiClientJSON
    .put(`/station-service/dashboard/vehicles/${id}${params}`, dto)
    .then((r) => r.data);
};

export const getVehicleStatuses = (): Promise<string[]> => {
  return apiClientJSON
    .get("/station-service/dashboard/vehicles/statuses")
    .then((r) => r.data);
};

export const getVehicleBrands = (): Promise<string[]> => {
  return apiClientJSON
    .get("/station-service/dashboard/vehicles/brands")
    .then((r) => r.data);
};

export const getLockStatuses = (): Promise<string[]> => {
  return apiClientJSON
    .get("/station-service/dashboard/locks/statuses")
    .then((r) => r.data);
};

export const getVehicleTypes = (): Promise<string[]> => {
  return apiClientJSON
    .get("/station-service/dashboard/vehicles/types")
    .then((r) => r.data);
};

export const unlockVehicle = (id: number): Promise<void> => {
  return apiClientJSON
    .post(`/station-service/dashboard/vehicles/${id}/unlock`)
    .then(() => {});
};

export const lockVehicle = (id: number): Promise<void> => {
  return apiClientJSON
    .post(`/station-service/dashboard/vehicles/${id}/lock`)
    .then(() => {});
};

export const alarmVehicle = (id: number): Promise<void> => {
  return apiClientJSON
    .post(`/station-service/dashboard/vehicles/${id}/alarm`)
    .then(() => {});
};

export const lockBattery = (code: string): Promise<unknown> => {
  return apiClientJSON
    .post(`/station-service/dashboard/vehicles/${code}/battery/lock`)
    .then((r) => r.data);
};

export const unlockBattery = (code: string): Promise<unknown> => {
  return apiClientJSON
    .post(`/station-service/dashboard/vehicles/${code}/battery/unlock`)
    .then((r) => r.data);
};

export const exportVehiclesToFile = (params: object) => {
  return apiClient.get("/station-service/dashboard/vehicles/export", {
    responseType: "blob",
    params,
  });
};

export interface VehicleExportListParams {
  page: number;
  size: number;
  search: string;
  sort: string;
  filter: string;
}

export const exportVehiclesList = (params: VehicleExportListParams) => {
  const endpoint = generateCustomEndpoint(
    "/station-service/dashboard/vehicles/export?",
    params.page,
    params.search,
    "",
    params.sort,
    params.filter,
    String(params.size)
  );
  return apiClient.get(endpoint, { responseType: "blob" });
};

export const exportVehiclesToFileExample = () => {
  return apiClient.get("/station-service/dashboard/vehicles/export/example", {
    responseType: "blob",
  });
};

export const importVehiclesFromFile = (
  cityId: number,
  stationCode: number | undefined,
  file: File
) => {
  const data = new FormData();
  data.append("file", file);

  const params: { cityId: number; stationCode?: number } = { cityId };
  if (stationCode !== undefined) {
    params.stationCode = stationCode;
  }

  return apiClientJSON.post(
    "/station-service/dashboard/vehicles/import",
    data,
    {
      params,
    }
  );
};

export const deleteVehicle = (vehicleId: number) => {
  return apiClient.delete(`/station-service/dashboard/vehicles/${vehicleId}`);
};

// --- Vehicle Import V2 (per-row results) ---

export interface VehicleImportRowResult {
  rowNumber: number;
  vehicleCode: string;
  success: boolean;
  error: string | null;
  data?: Record<string, string> | null;
}

export interface VehicleImportResult {
  totalRows: number;
  successCount: number;
  failedCount: number;
  rows: VehicleImportRowResult[];
}

export const importVehiclesV2 = (
  cityId: number,
  stationCode: number | null,
  file: File
): Promise<VehicleImportResult> => {
  const data = new FormData();
  data.append("file", file);

  const params: { cityId: number; stationCode?: number } = { cityId };
  if (stationCode != null) {
    params.stationCode = stationCode;
  }

  return apiClientJSON
    .post("/station-service/dashboard/vehicles/import", data, { params })
    .then((r) => r.data);
};

// --- Single Vehicle Create ---

export interface VehicleCreateParams {
  name?: string;
  code: number;
  type: string;
  status: string;
  brand: string;
  lockCode?: number;
  lockImei?: number;
  lockIccid?: string;
  lockBattery?: number;
  externalId?: string;
  additionalCode?: string;
  isBleSupported?: boolean;
  cityId: number;
  stationCode?: number;
}

export const createVehicle = (
  params: VehicleCreateParams
): Promise<Vehicle> => {
  return apiClientJSON
    .post("/station-service/dashboard/vehicles", params)
    .then((r) => r.data);
};

// --- Lock Reassignment ---

export const reassignLock = (
  vehicleId: number,
  lockId: number | null
): Promise<Vehicle> => {
  return apiClientJSON
    .put(`/station-service/dashboard/vehicles/${vehicleId}/lock`, { lockId })
    .then((r) => r.data);
};

export interface VehicleHistoryParams {
  page: number;
  size: number;
  vehicleCode: string;
  startDate: string;
  endDate: string;
}

export interface VehicleHistoryItem {
  journeyId: number;
  userId: number;
  username: string;
  journeyStatus: string;
  latitude: string;
  longitude: string;
  dateUpdated: string;
}

export interface VehicleHistoryResponse {
  content: VehicleHistoryItem[];
  totalPages: number;
  totalElements: number;
}

export const getVehicleHistory = (
  params: VehicleHistoryParams
): Promise<VehicleHistoryResponse> => {
  const { page, size, vehicleCode, startDate, endDate } = params;
  return apiClientJSON
    .get(
      `/journey-service/dashboard/journeys/vehicle/log?page=${page}&size=${size}&vehicleCode=${vehicleCode}&startDate=${startDate}&endDate=${endDate}`
    )
    .then((r) => r.data);
};
